import { useEffect, useState } from 'react';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Activity, 
  CheckCircle, 
  AlertTriangle, 
  XCircle,
  Clock,
  Zap,
  RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HealthPage = () => {
  const [healthData, setHealthData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/health`);
      setHealthData(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'degraded':
        return <AlertTriangle className="w-5 h-5 text-amber-400" />;
      case 'offline':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Activity className="w-5 h-5 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      online: 'bg-emerald-950/30 text-emerald-400 border-emerald-900/50',
      degraded: 'bg-amber-950/30 text-amber-400 border-amber-900/50',
      offline: 'bg-red-950/30 text-red-400 border-red-900/50',
    };
    return styles[status] || 'bg-slate-800 text-slate-300 border-slate-700';
  };

  const stats = {
    online: healthData.filter(h => h.status === 'online').length,
    degraded: healthData.filter(h => h.status === 'degraded').length,
    offline: healthData.filter(h => h.status === 'offline').length,
    avgUptime: healthData.length > 0 
      ? (healthData.reduce((sum, h) => sum + h.uptime_percentage, 0) / healthData.length).toFixed(2)
      : 0
  };

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
          <div>
            <Badge className="mb-4 bg-emerald-950/30 text-emerald-400 border-emerald-900/50">
              <Activity className="w-3.5 h-3.5 mr-1.5" />
              Live Status
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              Connector Health
            </h1>
            <p className="text-lg text-slate-400">
              Real-time status and uptime for all connectors
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchHealth}
            disabled={loading}
            className="border-slate-700"
            data-testid="refresh-health-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <CheckCircle className="w-8 h-8 text-emerald-400 mb-2" />
            <div className="text-3xl font-heading font-bold text-white">{stats.online}</div>
            <p className="text-sm text-slate-500">Online</p>
          </div>
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <AlertTriangle className="w-8 h-8 text-amber-400 mb-2" />
            <div className="text-3xl font-heading font-bold text-white">{stats.degraded}</div>
            <p className="text-sm text-slate-500">Degraded</p>
          </div>
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <XCircle className="w-8 h-8 text-red-400 mb-2" />
            <div className="text-3xl font-heading font-bold text-white">{stats.offline}</div>
            <p className="text-sm text-slate-500">Offline</p>
          </div>
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <Zap className="w-8 h-8 text-primary mb-2" />
            <div className="text-3xl font-heading font-bold text-white">{stats.avgUptime}%</div>
            <p className="text-sm text-slate-500">Avg Uptime</p>
          </div>
        </div>

        {/* Last Updated */}
        {lastUpdated && (
          <p className="text-sm text-slate-500 mb-6 flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Last updated: {lastUpdated.toLocaleTimeString()}
          </p>
        )}

        {/* Health Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {healthData.map((health) => (
            <div 
              key={health.connector_id}
              className="bg-card border border-white/5 rounded-xl p-6 hover:border-white/10 transition-colors"
              data-testid={`health-card-${health.connector_id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  {getStatusIcon(health.status)}
                  <h3 className="font-semibold text-white">{health.connector_name}</h3>
                </div>
                <Badge className={`${getStatusBadge(health.status)} capitalize`}>
                  {health.status}
                </Badge>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-400">Uptime</span>
                    <span className="text-white font-mono">{health.uptime_percentage}%</span>
                  </div>
                  <Progress 
                    value={health.uptime_percentage} 
                    className="h-2 bg-slate-800"
                  />
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Avg Response</span>
                  <span className="text-white font-mono">{health.avg_response_time_ms}ms</span>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Last Check</span>
                  <span className="text-slate-500">
                    {new Date(health.last_checked).toLocaleTimeString()}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HealthPage;
